// Global progress tracking for file exports (Word / PDF / Excel / PowerPoint).
//
// Exports can take a noticeable moment — some pull in large client-side
// generators via dynamic import(), others round-trip to the backend. To make it
// obvious that "something is happening", any export brackets its work with a
// task here, which drives a centered, blocking progress overlay
// (ExportProgressOverlay). This is deliberately NOT a toast: the work is modal
// and the user should not lose track of it in a corner popup.

export interface ExportTask {
  id: string;
  /** User-facing description of the current step, e.g. "Generating PDF…". */
  label: string;
}

type UpdateFn = (tasks: ExportTask[]) => void;

let tasks: ExportTask[] = [];
const listeners = new Set<UpdateFn>();
let nextId = 1;

const broadcast = (): void => {
  const snapshot = [...tasks];
  listeners.forEach((cb) => cb(snapshot));
};

export const exportProgressService = {
  start(label: string): string {
    const id = `export-${nextId++}`;
    tasks = [...tasks, { id, label }];
    broadcast();
    return id;
  },

  update(id: string, label: string): void {
    tasks = tasks.map((t) => (t.id === id ? { ...t, label } : t));
    broadcast();
  },

  finish(id: string): void {
    tasks = tasks.filter((t) => t.id !== id);
    broadcast();
  },

  getAll(): ExportTask[] {
    return tasks;
  },

  onUpdate(cb: UpdateFn): () => void {
    listeners.add(cb);
    return () => listeners.delete(cb);
  },
};

/**
 * Wait for the browser to paint at least one frame. Used so the overlay is
 * visible before a synchronous (or very fast) export blocks the main thread —
 * otherwise the user would see no feedback at all for instant downloads.
 */
const nextPaint = (): Promise<void> =>
  new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });

/**
 * Runs an export task while showing the progress overlay. The task receives a
 * `setLabel` callback so multi-step exports can describe their progress. The
 * overlay is always torn down (success or failure); errors are re-thrown so the
 * caller can surface them (inline message / notification) as appropriate.
 */
export const runExport = async <T>(
  label: string,
  task: (setLabel: (label: string) => void) => Promise<T> | T
): Promise<T> => {
  const id = exportProgressService.start(label);
  try {
    await nextPaint();
    return await task((next) => exportProgressService.update(id, next));
  } finally {
    exportProgressService.finish(id);
  }
};
